import type { Language } from '../../../common/types/i18n.js'
import type { NotionColor, NotionProject } from '../../../common/types/notion.js'
import { hexBadgeStyle } from '../../../common/utils/badge-color.js'
import { formatProjectCardDate } from '../../views/playtime/worktime/worktime-helpers.js'
import { getInitialsFromName } from '../../views/portal/utils/format.js'
import './project-card.scss'

export interface ProjectCardProps {
    project: NotionProject
    lang: Language
    onClick?: () => void
}

const statusColorMap: Record<string, string> = {
    default: '#e0e0e0',
    gray: '#9e9e9e',
    brown: '#795548',
    orange: '#ff9800',
    yellow: '#ffeb3b',
    green: '#4caf50',
    blue: '#2196f3',
    purple: '#9c27b0',
    pink: '#e91e63',
    red: '#f44336',
}

const getStatusColor = (color: NotionColor | null): string =>
    statusColorMap[color ?? 'default'] ?? statusColorMap.default

/** Notioni staatusevärv on andmevärv (sama mõlemas teemas); tekst valitakse heleduse järgi. */
const getStatusStyle = (color: NotionColor | null) =>
    hexBadgeStyle(getStatusColor(color)) ?? undefined

export const ProjectCard = ({ project, lang, onClick }: ProjectCardProps) => {
    // Collect all unique team members (owner + team), deduplicated by email
    const allMembers = [...(project.owner ? [project.owner] : []), ...project.team]
    const seen = new Set<string>()
    const uniqueMembers = allMembers.filter((m) => {
        if (seen.has(m.email)) return false
        seen.add(m.email)
        return true
    })

    return (
        <article
            className="project-card"
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className="project-card__header">
                {(project.startDate || project.endDate) && (
                    <p className="project-card__meta-date">
                        {formatProjectCardDate(project.startDate, project.endDate, lang)}
                    </p>
                )}
                <h3 className="project-card__title">{project.name}</h3>
                {project.status && (
                    <span
                        className="project-card__status-badge"
                        style={getStatusStyle(project.statusColor)}
                    >
                        {project.status}
                    </span>
                )}
            </div>

            {/* Team member avatars */}
            {uniqueMembers.length > 0 && (
                <div className="project-card__team-avatars">
                    {uniqueMembers.slice(0, 5).map((member) =>
                        member.avatarUrl ? (
                            <img
                                key={member.notionUserId}
                                className="project-card__avatar"
                                src={member.avatarUrl}
                                alt={member.name}
                                title={member.name}
                            />
                        ) : (
                            <div
                                key={member.notionUserId}
                                className="project-card__avatar project-card__avatar--placeholder"
                                title={member.name}
                            >
                                {getInitialsFromName(member.name)}
                            </div>
                        ),
                    )}
                    {uniqueMembers.length > 5 && (
                        <div className="project-card__avatar project-card__avatar--more">
                            +{uniqueMembers.length - 5}
                        </div>
                    )}
                </div>
            )}
        </article>
    )
}
